// packages/api-client/src/services/agentService.ts
import { getApiClient } from '../client';
import { handleApiError } from '../utils/handleApiError';
import type {
  AgentResponse,
  AgentCreatePayload,
  AgentUpdatePayload,
  PromptResponse,
  ApiSuccessResponse,
  ServiceResponse,
  ApiErrorResponse,
  UUID,
  AgentNotificationSettingsPayload,
  AgentNotificationSettings
} from '@dopomogai/types';

export const agentService = {
  listAgents: (): ServiceResponse<AgentResponse[]> => {
    return getApiClient().get<AgentResponse[]>(`/agents`)
      .then(response => response.data)
      .catch(err => handleApiError(err, 'listing agents'));
  },

  getAgent: (agentId: string): ServiceResponse<AgentResponse> => {
    return getApiClient().get<AgentResponse>(`/agents/${agentId}`)
      .then(response => response.data)
      .catch(err => handleApiError(err, `fetching agent ${agentId}`));
  },

  createAgent: (payload: AgentCreatePayload): ServiceResponse<AgentResponse> => {
    const { ...dataToSend } = payload;
    return getApiClient().post<AgentResponse>(`/agents`, dataToSend)
      .then(response => response.data)
      .catch(err => handleApiError(err, 'creating agent'));
  },

  updateAgent: (agentId: string, payload: AgentUpdatePayload): ServiceResponse<AgentResponse> => {
    const { ...dataToSend } = payload;
    return getApiClient().put<AgentResponse>(`/agents/${agentId}`, dataToSend)
      .then(response => response.data)
      .catch(err => handleApiError(err, `updating agent ${agentId}`));
  },

  deleteAgent: (agentId: string): ServiceResponse<ApiSuccessResponse> => {
    return getApiClient().delete(`/agents/${agentId}`)
      // FIX: Changed to match the ApiSuccessResponse interface
      .then(() => ({ status: 'success' } as const))
      .catch(err => handleApiError(err, `deleting agent ${agentId}`));
  },

  getAgentTools: (agentId: string): ServiceResponse<string[]> => {
    return getApiClient().get<string[]>(`/agents/${agentId}/tools`)
      .then(response => response.data)
      .catch(err => handleApiError(err, `fetching tools for agent ${agentId}`));
  },

  updateAgentTools: (agentId: string, toolSlugs: string[]): ServiceResponse<string[]> => {
    return getApiClient().put<string[]>(`/agents/${agentId}/tools`, toolSlugs)
      .then(response => response.data)
      .catch(err => handleApiError(err, `updating tools for agent ${agentId}`));
  },

  listPromptsForAgent: (agentId: string): ServiceResponse<PromptResponse[]> => {
    return getApiClient().get<PromptResponse[]>(`/agents/${agentId}/prompts`)
      .then(response => response.data)
      .catch(err => handleApiError(err, `listing prompts for agent ${agentId}`));
  },

  getActivePromptForAgent: async (agentId: string): ServiceResponse<PromptResponse | null> => {
    try {
      const response = await getApiClient().get<PromptResponse | null>(`/agents/${agentId}/prompts/active`);
      return response.data;
    } catch (err: any) {
      if (err.isAxiosError && err.response?.status === 404) {
        return null;
      }
      return handleApiError(err, `fetching active prompt for agent ${agentId}`);
    }
  },

  setActivePromptForAgent: (agentId: string, promptId: string): ServiceResponse<PromptResponse> => {
    return getApiClient().post<PromptResponse>(`/agents/${agentId}/prompts/${promptId}/activate`)
      .then(response => response.data)
      .catch(err => handleApiError(err, `activating prompt ${promptId} for agent ${agentId}`));
  },

  linkPromptToAgent: (agentId: string, promptId: string): ServiceResponse<ApiSuccessResponse> => {
    return getApiClient().put(`/agents/${agentId}/prompts/${promptId}/link`)
      // FIX: Changed to match the ApiSuccessResponse interface
      .then(() => ({ status: 'success' } as const))
      .catch(err => handleApiError(err, `linking prompt ${promptId} to agent ${agentId}`));
  },

  unlinkPromptFromAgent: (agentId: string, promptId: string): ServiceResponse<ApiSuccessResponse> => {
    return getApiClient().delete(`/agents/${agentId}/prompts/${promptId}/unlink`)
      // FIX: Changed to match the ApiSuccessResponse interface
      .then(() => ({ status: 'success' } as const))
      .catch(err => handleApiError(err, `unlinking prompt ${promptId} from agent ${agentId}`));
  },

  getAgentNotificationSettings: (agentId: UUID): ServiceResponse<AgentNotificationSettings> => {
    return getApiClient()
      .get<AgentNotificationSettings>(`/agents/${agentId}/notifications`)
      .then(res => res.data)
      .catch(err => handleApiError(err, `fetching notification settings for agent ${agentId}`));
  },

  updateAgentNotificationSettings: (
    agentId: UUID,
    payload: AgentNotificationSettingsPayload
  ): ServiceResponse<AgentNotificationSettings> => {
    return getApiClient()
      .put<AgentNotificationSettings>(`/agents/${agentId}/notifications`, payload)
      .then(res => res.data)
      .catch(err => handleApiError(err, `updating notification settings for agent ${agentId}`));
  },

  getAgentSettings: (agentId: UUID): ServiceResponse<Record<string, any>> =>
    getApiClient().get<Record<string, any>>(`/agents/${agentId}/settings`)
      .then(r => r.data)
      .catch(err => handleApiError(err, 'getting agent settings')),

  updateAgentSettings: (agentId: UUID, settings: Record<string, any>): ServiceResponse<Record<string, any>> =>
    getApiClient().put<Record<string, any>>(`/agents/${agentId}/settings`, { agent_settings: settings })
      .then(r => r.data)
      .catch(err => handleApiError(err, 'updating agent settings')),

};